import type { BusinessSegmentConditions, SegmentConditionGroups } from '@reach/shared-types/conditions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFeatureSurface } from '../FeatureSurfaceContext';
import {
  AI_SEGMENT_DRAFT_STORAGE_KEY,
  clearAiSegmentDraft,
  type AiSegmentDraft,
} from '../segments-tab/describe-segment-modal/types';
import { useAuth } from '../../../hooks/useAuth';
import {
  getTenantSelectionType,
  toTargetedTenantsFilter,
  type TenantSelectionType,
} from '../automations-tab/templates.types';
import { isLegacySegmentFormat } from '../segments-tab/segment-definitions-api';
import {
  useAvailableFields,
  useCreateSegmentFromTemplate,
  useSegmentDefinition,
  useUpdateSegment,
} from '../segments-tab/useSegmentTemplates';
import { AutomationStatusSummary } from '../edit-automation-template/draft-status/AutomationStatusSummary';
import { DraftBanner } from '../edit-automation-template/draft-status/DraftBanner';
import { StatusFilter, type TemplateListStatus } from '../automations-tab/templates-manager/types';
import styles from './EditSegment.module.css';
import { EditSegmentHeader } from './EditSegmentHeader';
import { SegmentDefinitionSection } from './SegmentDefinitionSection';
import { SegmentNameDescription } from './SegmentNameDescription';
import { SegmentTenantSelectionControl } from './SegmentTenantSelectionControl';
import { SegmentTenantSelectionDialog } from './SegmentTenantSelectionDialog';
import { parseConditionGroupsJson, parseLegacyConditionsJson } from './segmentDefinitionJson';
import { useSegmentTenantSelection } from './useSegmentTenantSelection';
import { useTenantDialog } from './useTenantDialog';

const DEFAULT_AVAILABLE_FIELDS = {
  contactFields: [] as string[],
  relatedSchemas: [],
};

const MAX_SEEDED_NAME_LENGTH = 120;
const MAX_SEEDED_DESCRIPTION_LENGTH = 500;

const SEGMENT_DRAFT_BANNER_TITLE = 'This segment is a draft';
const SEGMENT_DRAFT_BANNER_BODY =
  "It isn't synced to any tenant yet, so no tenant can target it. Sync it to tenants when you're ready.";

/**
 * sessionStorage survives logout and organization switches in the same tab, so a
 * draft is only honoured for the organization that generated it.
 */
function readStoredAiSegmentDraft(organizationId: string | undefined): AiSegmentDraft | undefined {
  const stored = sessionStorage.getItem(AI_SEGMENT_DRAFT_STORAGE_KEY);
  if (!stored) return undefined;
  try {
    const draft = JSON.parse(stored) as AiSegmentDraft;
    if (!organizationId || draft.organizationId !== organizationId) {
      clearAiSegmentDraft();
      return undefined;
    }
    return draft;
  } catch {
    clearAiSegmentDraft();
    return undefined;
  }
}

export function EditSegmentPage() {
  const { canonicalVersionId } = useParams<{ canonicalVersionId: string }>();
  const navigate = useNavigate();
  const featureSurface = useFeatureSurface();
  const location = useLocation();
  const { organization } = useAuth();

  // Determine if we're in create mode (no canonicalVersionId) or edit mode
  const isCreateMode = !canonicalVersionId;

  // Only fetch segment data in edit mode
  const {
    data: segment,
    isLoading: isLoadingSegment,
    error: segmentError,
  } = useSegmentDefinition(canonicalVersionId);

  const {
    data: availableFields = DEFAULT_AVAILABLE_FIELDS,
    isLoading: isLoadingFields,
    error: fieldsError,
  } = useAvailableFields();

  // Update mutation for edit mode
  const { mutate: updateSegment, isPending: isUpdating } = useUpdateSegment(canonicalVersionId || '', {
    onSuccess: () => {
      setSavedTenantSelectionType(tenantSelectionType);
      setSavedSelectedTenantIds(selectedTenantIds);
    },
  });

  // Create mutation for create mode
  const createSegmentMutation = useCreateSegmentFromTemplate({
    onSuccess: newCanonicalVersionId => {
      // Discard the AI draft mirror so the next "New Segment" starts clean.
      clearAiSegmentDraft();
      // After creating, navigate to edit mode for the new segment
      navigate(featureSurface.buildSegmentEditPath(newCanonicalVersionId), { replace: true });
    },
  });

  const {
    tenantRowData,
    conflicts,
    isLoading: isLoadingTenants,
  } = useSegmentTenantSelection({
    protectedTenantIds: segment?.businessIdsWithSegmentInActiveAutomation ?? [],
    conflictingAutomationNamesByBusinessId: segment?.conflictingAutomationNamesByBusinessId ?? {},
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contentsJson, setContentsJson] = useState('[]');
  const [isJsonValid, setIsJsonValid] = useState(true);
  const [editorMode, setEditorMode] = useState<'builder' | 'json' | 'preview'>('builder');
  const [formatType, setFormatType] = useState<'legacy' | 'new'>('new');
  // New segments start as Draft (selected + no tenants); edit mode is overwritten from the loaded segment.
  const initialSelectionType: TenantSelectionType = isCreateMode ? 'selected' : 'all';
  const [tenantSelectionType, setTenantSelectionType] =
    useState<TenantSelectionType>(initialSelectionType);
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [savedTenantSelectionType, setSavedTenantSelectionType] =
    useState<TenantSelectionType>(initialSelectionType);
  const [savedSelectedTenantIds, setSavedSelectedTenantIds] = useState<string[]>([]);
  const {
    isOpen: isTenantDialogOpen,
    open: openTenantDialog,
    close: closeTenantDialog,
  } = useTenantDialog();

  // Initialize form when segment loads (edit mode only)
  useEffect(() => {
    if (segment && !isCreateMode) {
      setName(segment.name);
      setDescription(segment.description || '');
      const loadedType = getTenantSelectionType(segment.targetedTenantsFilter);
      const loadedIds = segment.targetedTenantsFilter ?? [];
      setTenantSelectionType(loadedType);
      setSelectedTenantIds(loadedIds);
      setSavedTenantSelectionType(loadedType);
      setSavedSelectedTenantIds(loadedIds);

      // Legacy = contents (conditions) exist AND condition_groups is empty or not provided.
      const legacy = isLegacySegmentFormat({
        conditions: segment.contents,
        conditionGroups: segment.conditionGroups,
      });

      if (legacy) {
        setFormatType('legacy');
        setContentsJson(JSON.stringify(segment.contents, null, 2));
        setEditorMode('json');
      } else {
        setFormatType('new');
        const groups = segment.conditionGroups ?? [];
        setContentsJson(JSON.stringify(groups, null, 2));
      }
    }
  }, [segment, isCreateMode]);

  // Seed create mode once from an AI-generated draft. An effect is required
  // because the draft populates several independent state atoms (name,
  // description, format, JSON) and is mirrored to sessionStorage — neither is a
  // render-time derivation. Router state wins on first arrival; a refresh falls
  // back to the sessionStorage mirror.
  useEffect(() => {
    if (!isCreateMode) return;

    const draftFromRouter = (location.state as { aiSegmentDraft?: AiSegmentDraft } | null)
      ?.aiSegmentDraft;
    const routerDraftBelongsToOrg =
      draftFromRouter && organization && draftFromRouter.organizationId === organization.id;
    if (routerDraftBelongsToOrg) {
      sessionStorage.setItem(AI_SEGMENT_DRAFT_STORAGE_KEY, JSON.stringify(draftFromRouter));
    }

    const draft = routerDraftBelongsToOrg ? draftFromRouter : readStoredAiSegmentDraft(organization?.id);
    if (!draft) return;

    setName((draft.title ?? '').slice(0, MAX_SEEDED_NAME_LENGTH));
    setDescription((draft.description ?? '').slice(0, MAX_SEEDED_DESCRIPTION_LENGTH));
    setFormatType('new');
    setContentsJson(JSON.stringify(draft.conditionGroups, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seeds once per entry into create mode; re-running on location/organization would overwrite the partner's edits
  }, [isCreateMode]);

  const parsedConditions = useMemo((): BusinessSegmentConditions | null => {
    if (formatType !== 'legacy' || !contentsJson) return null;
    return parseLegacyConditionsJson(contentsJson, isJsonValid);
  }, [contentsJson, isJsonValid, formatType]);

  const parsedConditionGroups = useMemo((): SegmentConditionGroups | null => {
    if (formatType !== 'new' || !contentsJson) return null;
    return parseConditionGroupsJson(contentsJson);
  }, [contentsJson, formatType]);

  const conditionGroupsForPreview =
    formatType === 'new' && editorMode === 'builder'
      ? parsedConditionGroups
      : formatType === 'new' && isJsonValid && parsedConditionGroups
        ? parsedConditionGroups
        : null;

  const isTenantSelectionDirty =
    tenantSelectionType !== savedTenantSelectionType ||
    (tenantSelectionType === 'selected' &&
      (selectedTenantIds.length !== savedSelectedTenantIds.length ||
        [...selectedTenantIds].sort().join(',') !== [...savedSelectedTenantIds].sort().join(',')));

  const handleTenantSelectionChange = useCallback((type: TenantSelectionType, ids: string[]) => {
    setTenantSelectionType(type);
    setSelectedTenantIds(ids);
  }, []);

  const tenantFilterParam = {
    targetedTenantsFilter: toTargetedTenantsFilter(tenantSelectionType, selectedTenantIds),
  };

  const handleSave = () => {
    if (!isJsonValid) return;

    if (formatType === 'legacy') {
      const contents = parseLegacyConditionsJson(contentsJson, isJsonValid);
      if (contents == null) return;
      if (isCreateMode) {
        createSegmentMutation.mutate({
          name,
          description,
          contents,
          ...tenantFilterParam,
        });
      } else {
        updateSegment({
          name,
          description,
          contents,
          conditionGroups: null,
          ...tenantFilterParam,
        });
      }
    } else {
      const groups = parseConditionGroupsJson(contentsJson) ?? [];

      if (isCreateMode) {
        createSegmentMutation.mutate({
          name,
          description,
          conditionGroups: groups,
          ...tenantFilterParam,
        });
      } else {
        updateSegment({
          name,
          description,
          conditionGroups: groups,
          ...tenantFilterParam,
        });
      }
    }
  };

  const navigateToSegmentsList = () => {
    navigate(featureSurface.segmentsPath);
  };

  // Loading state - only show in edit mode when fetching existing segment
  if (!isCreateMode && isLoadingSegment) {
    return <p className={styles.loadingState}>Loading segment...</p>;
  }

  // Error state - only show in edit mode
  if (!isCreateMode && (segmentError || !segment)) {
    return <p className={styles.errorState}>Error loading segment. Please try again.</p>;
  }

  const isSaving = isCreateMode ? createSegmentMutation.isPending : isUpdating;

  const isSaveDisabled =
    isSaving ||
    isLoadingFields ||
    !name.trim() ||
    !description.trim() ||
    (editorMode === 'json' && !isJsonValid);

  const totalTenantCount = Object.keys(tenantRowData).length;
  const isDraft = tenantSelectionType === 'selected' && selectedTenantIds.length === 0;
  const segmentStatus: TemplateListStatus = isDraft ? StatusFilter.Draft : StatusFilter.Active;

  let availabilitySummary: string;
  if (isDraft) {
    availabilitySummary = 'Not synced to any tenant yet';
  } else if (tenantSelectionType === 'all') {
    availabilitySummary = `Available to all ${totalTenantCount} tenants (and new ones)`;
  } else {
    availabilitySummary = `Synced to ${selectedTenantIds.length} of ${totalTenantCount} tenants`;
  }

  return (
    <article className={styles.page}>
      <EditSegmentHeader
        onBack={navigateToSegmentsList}
        onSave={handleSave}
        isSaveDisabled={isSaveDisabled}
        isSaving={isSaving}
        saveLabel={isCreateMode ? 'Create Segment' : 'Save Segment'}>
        <SegmentTenantSelectionControl
          onOpen={openTenantDialog}
          isDirty={isTenantSelectionDirty}
          disabled={isSaving}
        />
      </EditSegmentHeader>

      <main>
        <AutomationStatusSummary status={segmentStatus} summary={availabilitySummary} />
        {isDraft && (
          <DraftBanner
            onSync={openTenantDialog}
            title={SEGMENT_DRAFT_BANNER_TITLE}
            body={SEGMENT_DRAFT_BANNER_BODY}
          />
        )}

        <SegmentNameDescription
          name={name}
          description={description}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          disabled={isSaving}
          defaultEditing={isCreateMode}
        />

        <SegmentDefinitionSection
          formatType={formatType}
          editorMode={editorMode}
          onEditorModeChange={setEditorMode}
          contentsJson={contentsJson}
          onContentsChange={setContentsJson}
          onValidationChange={setIsJsonValid}
          isJsonValid={isJsonValid}
          isSaving={isSaving}
          availableFields={availableFields}
          isLoadingFields={isLoadingFields}
          fieldsError={fieldsError}
          parsedConditions={parsedConditions}
          parsedConditionGroups={parsedConditionGroups}
          conditionGroupsForPreview={conditionGroupsForPreview}
        />
      </main>

      {isTenantDialogOpen && (
        <SegmentTenantSelectionDialog
          onClose={closeTenantDialog}
          onChange={handleTenantSelectionChange}
          tenantRowData={tenantRowData}
          conflicts={conflicts}
          tenantSelectionType={tenantSelectionType}
          selectedTenantIds={selectedTenantIds}
          disabled={isSaving}
          isLoading={isLoadingTenants}
        />
      )}
    </article>
  );
}

export default EditSegmentPage;
